import * as THREE from "three";
import { createArcDoorShape, createStraightDoorShape, createWaveDoorShape } from "./BeamTopShapeHelpers";
import { forwardRef, useMemo, useEffect } from "react";
import { useAppStore } from "@/stores/appStore";
import { rotateUVs } from "@/services/helpers/rotateUV";
import { BeamProps, BeamPropsVertical } from "@/services/interfaces";
import { config_base } from "@/data/config_base";
import useCopyTexture from "@/hooks/useCopyTexture";

const createExtrudeGeometry = (shape: THREE.Shape, extrudeSettings: any) => {
	return new THREE.ExtrudeGeometry(shape, extrudeSettings);
};

export const BeamTop = forwardRef<THREE.Mesh, BeamProps>(
	({ doorWidth, frameWidth, centerHeight, outerHeight, extrudeSettings, depth, pillarPosition, textures }, ref) => {
		const { gate } = useAppStore((state) => state.configurator);

		const shape = useMemo(() => {
			let shape;

			switch (gate.shape) {
				case "arc":
					shape = createArcDoorShape(doorWidth, outerHeight, centerHeight, frameWidth);
					break;

				case "straight":
					shape = createStraightDoorShape(doorWidth, outerHeight, centerHeight, frameWidth);
					break;

				case "wave":
					shape = createWaveDoorShape(doorWidth, outerHeight, centerHeight, frameWidth);
					break;

				default:
					shape = createArcDoorShape(doorWidth, outerHeight, centerHeight, frameWidth);
					break;
			}

			return shape;
		}, [doorWidth, frameWidth, centerHeight, outerHeight, gate.shape]);

		// Create geometry and dispose of the old one before re-creating
		const geometry = useMemo(() => {
			const newGeometry = createExtrudeGeometry(shape, extrudeSettings);

			rotateUVs(newGeometry, {
				front: 90,
				left: 270,
				right: 270,
				back: 90,
				top: 90,
				bottom: 270,
			});

			return newGeometry;
		}, [shape, extrudeSettings]);

		// Dispose of geometry when component unmounts or before creating a new one
		useEffect(() => {
			return () => {
				geometry.dispose();
			};
		}, [geometry]); // Cleanup previous geometry

		return (
			<mesh castShadow ref={ref} receiveShadow geometry={geometry} position={[0, 0, -(depth / 2)]}>
				<meshStandardMaterial
					{...textures}
					color={gate.selectedMaterials[gate.id]?.settings.color}
					roughness={gate.selectedMaterials[gate.id]?.settings.roughness}
					normalScale={new THREE.Vector2(1, pillarPosition === "right" ? -1 : 1)}
				/>
			</mesh>
		);
	}
);

BeamTop.displayName = "BeamTop";

export const BeamBottom = forwardRef<THREE.Mesh, BeamProps>(
	({ doorWidth, frameWidth, centerHeight, extrudeSettings, depth, pillarPosition, textures }, ref) => {
		const { gate } = useAppStore((state) => state.configurator);

		const shape = useMemo(() => {
			let shape = new THREE.Shape()
				.moveTo(frameWidth, frameWidth)
				.lineTo(doorWidth - frameWidth, frameWidth)
				.lineTo(doorWidth - frameWidth, 0)
				.lineTo(frameWidth, 0)
				.closePath();

			return shape;
		}, [doorWidth, frameWidth, centerHeight, outerHeight]);

		// Create geometry and dispose of the old one before re-creating
		const geometry = useMemo(() => {
			const newGeometry = createExtrudeGeometry(shape, extrudeSettings);
			rotateUVs(newGeometry, {
				front: 90,
				left: 0,
				right: 270,
				back: 90,
				top: 90,
				bottom: 90,
			});
			return newGeometry;
		}, [shape, extrudeSettings]);

		// Dispose of geometry when component unmounts or before creating a new one
		useEffect(() => {
			return () => {
				geometry.dispose();
			};
		}, [geometry]);

		return (
			<mesh castShadow receiveShadow ref={ref} geometry={geometry} position={[0, 0, -(depth / 2)]}>
				<meshStandardMaterial
					{...textures}
					color={gate.selectedMaterials[gate.id]?.settings.color}
					roughness={gate.selectedMaterials[gate.id]?.settings.roughness}
					normalScale={new THREE.Vector2(1, pillarPosition === "right" ? -1 : 1)}
				/>
			</mesh>
		);
	}
);

BeamBottom.displayName = "BeamBottom";

export const BeamVertical = forwardRef<THREE.Mesh, BeamPropsVertical>(
	({ doorWidth, frameWidth, height, extrudeSettings, crownPos = "left", position, pillarPosition, textures }, ref) => {
		let shape;
		const { gate } = useAppStore((state) => state.configurator);
		const crown = gate.selectedModifiers.find((modifier) => modifier.location === "beam_vertical_top");

		if (crown && crown.id === "crown") {
			shape = useMemo(() => {
				if (crownPos === "left") {
					shape = new THREE.Shape()
						.moveTo(0, 0)
						.lineTo(0, height + 0.04)
						.lineTo(frameWidth / 2, height + 0.04)
						.quadraticCurveTo(frameWidth / 2, height + 0.02, frameWidth, height + 0.02)
						.lineTo(frameWidth, height + 0.02)
						.lineTo(frameWidth, height)
						.lineTo(frameWidth, 0)
						.closePath();
				} else {
					shape = new THREE.Shape()
						.moveTo(0, 0)
						.lineTo(0, height + 0.01)
						.quadraticCurveTo(frameWidth / 2, height + 0.01, frameWidth / 2, height + 0.04)
						.lineTo(frameWidth / 2, height + 0.04)
						.lineTo(frameWidth, height + 0.04)
						.lineTo(frameWidth, height)
						.lineTo(frameWidth, 0)
						.closePath();
				}
				return shape;
			}, [doorWidth, frameWidth, height, outerHeight, gate.selectedModifiers]);
		} else {
			shape = useMemo(() => {
				return new THREE.Shape()
					.moveTo(0, 0)
					.lineTo(0, height)
					.lineTo(frameWidth, height)
					.lineTo(frameWidth, 0)
					.closePath();
			}, [doorWidth, frameWidth, height, outerHeight, gate.selectedModifiers]);
		}

		// Create geometry and dispose of the old one before re-creating
		const geometry = useMemo(() => {
			const newGeometry = createExtrudeGeometry(shape, extrudeSettings);

			rotateUVs(newGeometry, {
				front: 180,
				left: 270,
				right: 270,
				back: 180,
				top: 90,
				bottom: 0,
			});

			return newGeometry;
		}, [shape, extrudeSettings]);

		// Dispose of geometry when component unmounts or before creating a new one
		useEffect(() => {
			return () => {
				geometry.dispose();
			};
		}, [geometry]); // Cleanup previous geometry

		return (
			<mesh castShadow receiveShadow ref={ref} geometry={geometry} position={position}>
				<meshStandardMaterial
					{...textures}
					color={gate.selectedMaterials[gate.id]?.settings.color}
					roughness={gate.selectedMaterials[gate.id]?.settings.roughness}
					normalScale={new THREE.Vector2(pillarPosition === "left" ? -1 : 1, 1)}
				/>
			</mesh>
		);
	}
);

BeamVertical.displayName = "BeamVertical";
